import { Button } from '@/components/ui/Button'
import { t, getCurrentLanguage } from '@/utils/i18n'
import { trackEvent } from '@/utils/analytics'

/**
 * The single source-of-truth lead form, used by both the dedicated /contact
 * page and the homepage final-CTA section. Fields and the submit handler are
 * defined once here so the payload always matches the Apps Script backend
 * (name, email, company, phone, industry, message — keyed by name) and the
 * `generate_lead` analytics event fires identically from anywhere.
 *
 * Returns just the <form>; callers wrap it (card, heading, layout) as needed.
 *
 * Two anti-spam signals ride along in the payload — a honeypot field and the
 * time from render to submit. Neither is visible or does anything a visitor can
 * feel: the form itself is unchanged. They are *signals*, not gates. Nothing is
 * blocked here, and the backend never rejects a submission either — it only
 * decides how loudly to notify. See docs/done/CONTACT-FORM-SPAM.md.
 */
export function ContactForm(): HTMLFormElement {
  const form = document.createElement('form')
  form.className = 'space-y-6'

  // Read once at render so the submit handler can report how long the visitor
  // took. Bots that post the instant the DOM is ready score on `too-fast`.
  const renderedAt = Date.now()

  // Name & Email Row
  const row1 = document.createElement('div')
  row1.className = 'grid grid-cols-1 md:grid-cols-2 gap-6'

  const nameField = createFormField('name', t('contactPage.fullName'), 'text', t('contactPage.fullNamePlaceholder'), true)
  const emailField = createFormField('email', t('contactPage.emailAddress'), 'email', t('contactPage.emailPlaceholder'), true)

  row1.appendChild(nameField)
  row1.appendChild(emailField)

  // Company & Phone Row
  const row2 = document.createElement('div')
  row2.className = 'grid grid-cols-1 md:grid-cols-2 gap-6'

  const companyField = createFormField('company', t('contactPage.companyName'), 'text', t('contactPage.companyPlaceholder'), true)
  const phoneField = createFormField('phone', t('contactPage.phoneNumber'), 'tel', t('contactPage.phonePlaceholder'), false)

  row2.appendChild(companyField)
  row2.appendChild(phoneField)

  // Industry Select
  const industryField = createIndustrySelect()

  // Message Textarea
  const messageField = createMessageField()

  // Privacy Checkbox
  const privacyField = createPrivacyCheckbox()

  // Honeypot (invisible to people, appended last — see createHoneypotField)
  const honeypotField = createHoneypotField()

  // Submit Button
  const submitButton = Button({
    children: t('contactPage.sendMessage'),
    variant: 'primary',
    size: 'lg',
    className: 'w-full shadow-lg hover:shadow-xl',
    type: 'submit',
  }) as HTMLButtonElement

  // Error feedback (inline, only for errors)
  const feedbackEl = document.createElement('div')
  feedbackEl.className = 'hidden text-center text-sm font-medium py-3 px-4 rounded-lg'

  form.addEventListener('submit', async (e) => {
    e.preventDefault()

    const formData = new FormData(form)
    const payload: Record<string, string> = {}
    formData.forEach((value, key) => { payload[key] = value.toString() })
    payload.elapsedMs = String(Date.now() - renderedAt)

    // Only people never touch the honeypot, so a filled one means a bot — or a
    // password manager that autofilled a hidden field. We can't tell the two
    // apart here, so we still submit (the backend records every submission and
    // a real lead must not be dropped) and only hold back the conversion event.
    const honeypotFilled = (payload[HONEYPOT_FIELD] || '').trim() !== ''

    // Loading state
    submitButton.disabled = true
    submitButton.textContent = t('contactPage.submitting')
    feedbackEl.className = 'hidden'

    const scriptUrl = import.meta.env.VITE_APPS_SCRIPT_URL as string | undefined

    if (!scriptUrl) {
      console.error('VITE_APPS_SCRIPT_URL is not set.')
      showFeedback(feedbackEl, 'error', t('contactPage.errorConfig'))
      submitButton.disabled = false
      submitButton.textContent = t('contactPage.sendMessage')
      return
    }

    try {
      await fetch(scriptUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload),
      })

      form.reset()
      showSuccessModal()
      // Key conversion: a real lead was submitted to the backend.
      // NOT `language`: GA4 collects a parameter of that name automatically on
      // every event (the browser's UI language) and refuses to expose it as a
      // registrable custom dimension, so our value was silently unusable.
      // `site_language` is the language toggle the visitor was actually reading in.
      if (!honeypotFilled) {
        trackEvent('generate_lead', {
          form_industry: payload.industry || '(none)',
          site_language: getCurrentLanguage(),
        })
      }
    } catch (_err) {
      showFeedback(feedbackEl, 'error', t('contactPage.errorSubmit'))
      trackEvent('lead_submit_error', { error: String(_err) })
    } finally {
      submitButton.disabled = false
      submitButton.textContent = t('contactPage.sendMessage')
    }
  })

  form.appendChild(row1)
  form.appendChild(row2)
  form.appendChild(industryField)
  form.appendChild(messageField)
  form.appendChild(privacyField)
  form.appendChild(submitButton)
  form.appendChild(feedbackEl)
  // Last on purpose: `space-y-6` puts its margin on every child but the first,
  // so inserting anywhere earlier would shift the visible fields.
  form.appendChild(honeypotField)

  return form
}

/**
 * Field name for the honeypot. Deliberately NOT `website`, `url`, or `company2`
 * — password managers recognise those and autofill them even when hidden, which
 * would flag real leads. `contact_ref` matches no autofill token.
 * Must stay in sync with `SPAM.HONEYPOT_FIELD` in the Apps Script backend.
 */
const HONEYPOT_FIELD = 'contact_ref'

/**
 * A decoy field: off-screen, unreachable by tab, and hidden from screen readers,
 * so no person ever sees or fills it — but it sits in the DOM where a form-filling
 * bot will find it. Positioned off-screen rather than `display: none` because
 * bots increasingly skip fields the latter hides.
 *
 * Text here is intentionally not run through `t()`: it is never rendered to a
 * visitor, and translating a decoy would only make it easier to fingerprint.
 */
function createHoneypotField(): HTMLElement {
  const div = document.createElement('div')
  div.className = 'absolute -left-[9999px] w-px h-px overflow-hidden'
  div.setAttribute('aria-hidden', 'true')

  const label = document.createElement('label')
  label.htmlFor = HONEYPOT_FIELD
  label.textContent = 'Reference'

  const input = document.createElement('input')
  input.type = 'text'
  input.id = HONEYPOT_FIELD
  input.name = HONEYPOT_FIELD
  input.tabIndex = -1
  input.autocomplete = 'off'

  div.appendChild(label)
  div.appendChild(input)

  return div
}

function createFormField(
  name: string,
  label: string,
  type: string,
  placeholder: string,
  required: boolean
): HTMLElement {
  const div = document.createElement('div')

  const labelEl = document.createElement('label')
  labelEl.htmlFor = name
  labelEl.textContent = label + (required ? ' *' : '')
  labelEl.className = 'block mb-2 text-sm font-medium text-slate-700'

  const input = document.createElement('input')
  input.type = type
  input.id = name
  input.name = name
  input.placeholder = placeholder
  input.required = required
  input.className = 'w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all hover:bg-white'

  div.appendChild(labelEl)
  div.appendChild(input)

  return div
}

function createIndustrySelect(): HTMLElement {
  const div = document.createElement('div')

  const label = document.createElement('label')
  label.htmlFor = 'industry'
  label.textContent = t('contactPage.industry') + ' *'
  label.className = 'block mb-2 text-sm font-medium text-slate-700'

  const select = document.createElement('select')
  select.id = 'industry'
  select.name = 'industry'
  select.required = true
  select.className = 'w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all hover:bg-white'

  const options = [
    { value: '', textKey: 'contactPage.selectIndustry' },
    { value: 'energy', textKey: 'contactPage.industryEnergy' },
    { value: 'manufacturing', textKey: 'contactPage.industryManufacturing' },
    { value: 'aluminum', textKey: 'contactPage.industryAluminum' },
    { value: 'district-heating', textKey: 'contactPage.industryDistrictHeating' },
    { value: 'chemical', textKey: 'contactPage.industryChemical' },
    { value: 'other', textKey: 'contactPage.industryOther' },
  ]

  options.forEach(opt => {
    const option = document.createElement('option')
    option.value = opt.value
    option.textContent = t(opt.textKey)
    select.appendChild(option)
  })

  div.appendChild(label)
  div.appendChild(select)

  return div
}

function createMessageField(): HTMLElement {
  const div = document.createElement('div')

  const label = document.createElement('label')
  label.htmlFor = 'message'
  label.textContent = t('contactPage.message') + ' *'
  label.className = 'block mb-2 text-sm font-medium text-slate-700'

  const textarea = document.createElement('textarea')
  textarea.id = 'message'
  textarea.name = 'message'
  textarea.placeholder = t('contactPage.messagePlaceholder')
  textarea.rows = 6
  textarea.required = true
  textarea.className = 'w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none hover:bg-white'

  div.appendChild(label)
  div.appendChild(textarea)

  return div
}

function createPrivacyCheckbox(): HTMLElement {
  const div = document.createElement('div')
  div.className = 'flex items-start gap-3'

  const checkbox = document.createElement('input')
  checkbox.type = 'checkbox'
  checkbox.id = 'privacy'
  checkbox.required = true
  checkbox.className = 'mt-1 w-4 h-4 text-orange-600 rounded border-slate-300 focus:ring-orange-500'

  const label = document.createElement('label')
  label.htmlFor = 'privacy'
  label.textContent = t('contactPage.privacyConsent') + ' *'
  label.className = 'text-sm text-slate-600'

  div.appendChild(checkbox)
  div.appendChild(label)

  return div
}

function showFeedback(el: HTMLElement, type: 'success' | 'error', message: string): void {
  el.textContent = message
  el.className = type === 'success'
    ? 'text-center text-sm font-medium py-3 px-4 rounded-lg bg-green-50 text-green-700 border border-green-200'
    : 'text-center text-sm font-medium py-3 px-4 rounded-lg bg-red-50 text-red-700 border border-red-200'
}

function showSuccessModal(): void {
  // Overlay
  const overlay = document.createElement('div')
  overlay.className = 'fixed inset-0 z-50 flex items-center justify-center p-4'
  overlay.style.backgroundColor = 'rgba(0,0,0,0.55)'
  overlay.style.backdropFilter = 'blur(4px)'
  overlay.style.opacity = '0'
  overlay.style.transition = 'opacity 0.25s ease'

  // Modal card
  const modal = document.createElement('div')
  modal.className = 'relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-10 text-center'
  modal.style.transform = 'translateY(16px)'
  modal.style.transition = 'transform 0.25s ease'

  // Check icon circle
  const iconCircle = document.createElement('div')
  iconCircle.className = 'mx-auto mb-6 w-20 h-20 rounded-full flex items-center justify-center'
  iconCircle.style.backgroundColor = '#FEF0EC'
  iconCircle.innerHTML = `
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#EF5730" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M20 6L9 17l-5-5"/>
    </svg>`

  // Title
  const title = document.createElement('h3')
  title.className = 'text-2xl font-bold text-slate-900 mb-3'
  title.textContent = t('contactPage.successTitle')

  // Body text
  const body = document.createElement('p')
  body.className = 'text-slate-500 text-base leading-relaxed mb-8'
  body.textContent = t('contactPage.thankYouMessage')

  // Close button
  const closeBtn = document.createElement('button')
  closeBtn.type = 'button'
  closeBtn.className = 'w-full py-3 px-6 rounded-full font-semibold text-white text-base transition-all duration-200 hover:opacity-90 active:scale-95'
  closeBtn.style.backgroundColor = '#EF5730'
  closeBtn.textContent = t('contactPage.confirm')

  const close = () => {
    overlay.style.opacity = '0'
    modal.style.transform = 'translateY(16px)'
    setTimeout(() => overlay.remove(), 250)
  }

  closeBtn.addEventListener('click', close)
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close() })
  document.addEventListener('keydown', function onKey(e) {
    if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onKey) }
  })

  modal.appendChild(iconCircle)
  modal.appendChild(title)
  modal.appendChild(body)
  modal.appendChild(closeBtn)
  overlay.appendChild(modal)
  document.body.appendChild(overlay)

  // Animate in
  requestAnimationFrame(() => {
    overlay.style.opacity = '1'
    modal.style.transform = 'translateY(0)'
  })
}
